// ============================================
// Character Panel — single view: paper-doll (dressed) ⇄ nude, status badges
// 0.2.9: the legacy Nude/Clothed tab bar + 5-slot/accessory grids were
// removed. The eye button toggles dressed⇄nude, the play button toggles the
// nude animation, and hair/nails/laser/HRT timers show as badges under the
// figure.
// ============================================

import { variableManager } from '../engine/variables.js';
import { storyManager } from '../engine/story.js';
import { resolveImage } from '../image-loader.js';
import { L } from '../systems/localization.js';
import { audioManager } from '../systems/audio.js';
import { appearanceState } from '../systems/appearance.js';

// ============================================
// Status badges — hair / nails / laser / HRT timers under the figure.
// Formerly these were computed into hidden .acc-slot labels (the slot/accessory
// grid was retired in 0.3); now they render straight into the visible
// #doll-status-row. refreshAccessories() keeps its name + call-sites
// (ITEM_EQUIPPED / BUFFS_RECALCULATED / STORY_CHOICES_READY / load).
// ============================================
function refreshAccessories() {
    const story = storyManager.currentStory;
    const statusRow = document.getElementById('doll-status-row');
    if (!statusRow) return;
    if (!story) { statusRow.innerHTML = ''; statusRow.classList.add('hidden'); return; }

    const iv = (v) => { try { return story.variablesState[v] || 0; } catch(e) { return 0; } };
    const ib = (v) => { try { const val = story.variablesState[v]; return val === true || val === 1; } catch(e) { return false; } };
    // Interpolate {0} if present, else append "(token)".
    const fmt = (key, fallback, token) => {
        const base = L.get(key) || fallback;
        return base.includes('{0}') ? base.replace('{0}', token) : `${base} (${token})`;
    };

    const day = iv('Day');
    const badges = [];

    // Hair extensions / wig
    if (ib('has_long_hair') || ib('wearing_wig')) {
        if (ib('wearing_wig')) {
            badges.push(L.get('char.acc.hair_wig') || 'Wig');
        } else if (ib('hair_permanent') || ib('auto_hair')) {
            // No decay (transplant) / self-resetting upkeep (Vanity T3 Auto-Hair):
            // a countdown or "Fading!" here is a false salon-urgency signal.
            badges.push(L.get('char.acc.hair_permanent') || 'Hair');
        } else {
            const daysLeft = Math.max(0, 7 - (day - iv('hair_last_touchup_day')));
            badges.push(daysLeft <= 0
                ? (L.get('char.acc.hair_fading') || 'Fading!')
                : fmt('char.acc.extensions', 'Hair ({0}d)', daysLeft));
        }
    }
    // Nails
    if (iv('nail_tier') > 0) {
        const daysLeft = iv('nail_days_remaining');
        badges.push(daysLeft > 0
            ? fmt('char.acc.nails_timer', 'Nails ({0}d)', daysLeft)
            : (L.get('char.acc.nails') || 'Nails'));
    }
    // Laser hair removal (5-session course, then regrow watch)
    const laser = iv('laser_sessions');
    if (laser > 0) {
        if (laser >= 5 && ib('body_hairless')) badges.push(L.get('char.acc.laser_done') || 'Smooth');
        else if (laser >= 5) badges.push(L.get('char.acc.laser_regrow') || 'Regrowing!');
        else badges.push(fmt('char.acc.laser', 'Laser ({0})', `${laser}/5`));
    }
    // HRT injection cooldown (3-day, mirrors the Clinic.ink Treatment gate)
    if (iv('hrt_stage') > 0) {
        const daysUntil = Math.max(0, (iv('hrt_last_injection_day') + 3) - day);
        badges.push(daysUntil > 0
            ? fmt('char.acc.hrt_timer', 'HRT ({0}d)', daysUntil)
            : (L.get('char.acc.hrt_due') || 'HRT (due)'));
    }

    statusRow.innerHTML = '';
    for (const text of badges) {
        const b = document.createElement('span');
        b.className = 'doll-status-badge';
        b.textContent = text;
        statusRow.appendChild(b);
    }
    statusRow.classList.toggle('hidden', badges.length === 0);
}

// Legacy entry point still called on equip / buff-recalc / choices-ready /
// load. The paper-doll re-renders via its own event listeners (paper-doll.js);
// here we only refresh the status badges.
function updateCharacterPanelSlots() {
    refreshAccessories();
}

// ============================================
// Nude View Toggle
// ============================================
// Cross-fade choreography: hides currently-visible content, swaps DOM after
// 200ms, then plays a reveal animation. Internal refreshes (animate toggle,
// video fallback) call _applyNudeView directly to skip the choreography.
let _toggleInFlight = false;

function toggleNudeView(isNude) {
    if (_toggleInFlight) return;
    const rightCol = document.getElementById('right-column');
    if (!rightCol) return;

    // Identify currently-visible elements to fade out
    const fadeTargets = [];
    const dollBox = document.getElementById('paper-doll-panel');
    const existingNude = rightCol.querySelector('.nude-view');
    if (isNude) {
        if (dollBox && dollBox.style.display !== 'none') fadeTargets.push(dollBox);
    } else {
        if (existingNude) fadeTargets.push(existingNude);
    }

    // No-op fast path: if no visible content matches, just apply directly
    if (fadeTargets.length === 0) {
        _applyNudeView(isNude);
        _playRevealAnim(rightCol, isNude);
        return;
    }

    _toggleInFlight = true;
    try { audioManager.playSFX('quick_transition1'); } catch(e) {}

    fadeTargets.forEach(el => el.classList.add('char-fade-out'));

    setTimeout(() => {
        fadeTargets.forEach(el => el.classList.remove('char-fade-out'));
        _applyNudeView(isNude);
        _playRevealAnim(rightCol, isNude);
        _toggleInFlight = false;
    }, 200);
}

function _playRevealAnim(rightCol, isNude) {
    const target = isNude
        ? rightCol.querySelector('.nude-view')
        : document.getElementById('paper-doll-panel');
    if (!target) return;
    target.classList.remove('char-fade-in');
    // Force reflow so the animation re-triggers when toggling rapidly
    void target.offsetWidth;
    target.classList.add('char-fade-in');
    setTimeout(() => target.classList.remove('char-fade-in'), 500);
}

// Session-scoped: nude videos failed after retry this session — render
// sprites without persisting the CharAnimateNude pref. Cleared when the
// player explicitly clicks Animate again.
let _nudeVideoSessionFallback = false;
// Doll-base nude still failed to load THIS session (genuine 404, not just
// tar-not-ready) -> degrade to the legacy 5-slot sprite stack without thrashing.
let _nudeDollStillFailed = false;

/**
 * Fully release every nude-view <video> inside `panel` before it's detached.
 * The nude clip re-renders on every stat/outfit change while the panel is open
 * (innerHTML='' below) and is dropped on the clothed-view switch (panel.remove).
 * Detach alone neither pauses nor frees the decoded buffer — a stat-heavy
 * session in nude view piles up orphaned WebM buffers until GC. These clips are
 * always muted + controls-less so there's no audio-zombie risk (unlike gallery
 * / story-content), but the buffer-reclaim half is the same class — mirror the
 * story-content _releaseVideos teardown. Safe on the happy path: a fresh <video>
 * is created immediately after.
 */
function _releaseNudeVideos(panel) {
    if (!panel) return;
    panel.querySelectorAll('video').forEach(v => {
        try {
            v.pause();
            v.muted = true;
            v.removeAttribute('src');
            v.load();
        } catch (e) { /* detached mid-flight */ }
    });
}

// Legacy nude representation: the 5-slot stacked body sprites
// (char_upper/lower/penis/butt/feet). Retained as the doll-still fallback
// (masc, tar-not-ready, genuine 404) and the A/B comparison path
// (PnO_NudeDollStill='0') until the doll-still set is verified + the videos
// retired (NUDE_STILLS.md § Retirement).
function _renderLegacyNudeSprites(nudePanel, sprites) {
    for (const s of sprites) {
        const slot = document.createElement('div');
        slot.className = 'char-slot nude-slot';
        const img = document.createElement('img');
        img.src = `assets/images/Characters/${s.src}.png`;
        img.alt = s.label;
        img.style.cssText = 'width:100%;height:100%;object-fit:cover;border-radius:6px;';
        img.onerror = () => { img.style.display = 'none'; };
        const label = document.createElement('span');
        label.className = 'char-slot-label';
        label.textContent = s.label;
        slot.appendChild(img);
        slot.appendChild(label);
        nudePanel.appendChild(slot);
    }
}

function _applyNudeView(isNude) {
    const rightCol = document.getElementById('right-column');
    if (!rightCol) return;

    const dollBox = document.getElementById('paper-doll-panel');
    let nudePanel = rightCol.querySelector('.nude-view');

    if (isNude) {
        // Hide the dressed doll, show the nude view in the same framed slot.
        if (dollBox) dollBox.style.display = 'none';

        if (!nudePanel) {
            nudePanel = document.createElement('div');
            nudePanel.className = 'nude-view';
            // Mount right after the (now hidden) doll box so the nude clip
            // occupies the same slot inside .char-image-stage; the overlay
            // eye/play controls float above both.
            if (dollBox) dollBox.after(nudePanel);
            else rightCol.appendChild(nudePanel);
        }

        // Determine fem tier suffix
        const fem = variableManager.getValue('Femininity') || 0;
        const arousal = variableManager.getValue('Arousal') || 0;
        const chastity = variableManager.getFlag('wearing_chastity');
        // wearing_plug is NOT in OBSERVED_VARIABLES so getFlag() always returns
        // false. Read story.variablesState directly per save-state-drift.md §7
        // 'UI panels reading non-observed Ink VAR'. (Audit 2026-05-23.)
        let plug = false;
        try {
            const sm = (typeof window !== 'undefined') ? window._sm : null;
            const v = sm?.currentStory?.variablesState?.['wearing_plug'];
            plug = (v === true || v === 1);
        } catch (e) {}

        let femSuffix = '';
        if (fem >= 75) femSuffix = '_fem75';
        else if (fem >= 50) femSuffix = '_fem50';
        else if (fem >= 25) femSuffix = '_fem25';

        // Arousal state for penis
        let penisState = '';
        if (arousal >= 60) penisState = '_dripping';
        else if (arousal >= 30) penisState = '_hard';

        let chastSuffix = chastity ? '_chastity' : '';

        // Build nude sprite grid (matching Unity CharacterPanelController)
        const sprites = [
            { label: 'Upper Body', src: `char_upper${femSuffix}` },
            { label: 'Lower Body', src: `char_lower${femSuffix}` },
            { label: 'Front', src: `char_penis${penisState}${femSuffix}${chastSuffix}` },
        ];
        if (plug) {
            sprites.push({ label: 'Back', src: `char_butt_plug${femSuffix}` });
        } else {
            sprites.push({ label: 'Back', src: `char_butt${femSuffix}` });
        }
        sprites.push({ label: 'Feet', src: `char_feet${femSuffix}` });

        // Animate toggle (video vs sprite).
        // D13: the looping nude videos used to sit behind this unlabeled toggle
        // that almost nobody found (default was static), so the 16 rendered
        // state loops went unseen. Default to ANIMATED the first time (when the
        // player hasn't chosen yet) unless Reduce Motion is on. The "Static"
        // button still switches back and persists the choice, and a missing
        // video variant falls back to the sprite paperdoll via onerror.
        const _animPref = localStorage.getItem('CharAnimateNude');
        const _reduceMotion = localStorage.getItem('PnO_ReduceMotion') === '1';
        // _nudeVideoSessionFallback: videos failed THIS session (transient
        // CDN hiccup) — degrade to sprites for now without persisting the
        // pref. The Animate button clears it for an explicit retry.
        const animateNude = (_animPref === 'true' || (_animPref === null && !_reduceMotion)) && !_nudeVideoSessionFallback;

        _releaseNudeVideos(nudePanel);
        nudePanel.innerHTML = '';

        // Appearance axes shared by the animated clip-picker AND the doll-base
        // still. has_long_hair/wearing_wig are observed flags; has_visible_breasts
        // / presenting_masc / masc_path are Ink-only -> read story.variablesState
        // directly per save-state-drift.md §7 (same pattern as wearing_plug above).
        const longhair = variableManager.getFlag('has_long_hair') || variableManager.getFlag('wearing_wig');
        const aroused = arousal >= 30;
        let boobs = false, mascClip = false;
        try {
            const vs = (typeof window !== 'undefined') ? window._sm?.currentStory?.variablesState : null;
            const vb = vs?.['has_visible_breasts'];
            boobs = (vb === true || vb === 1);
            // presenting_masc alone is TRUE for any non-fem-dressed PC (trap, see
            // MASC_AXIS.md) — gate the masc body on the masc route being active.
            const pm = vs?.['presenting_masc'];
            const mp = vs?.['masc_path'];
            mascClip = (pm === true || pm === 1) && !!mp && mp !== 0;
        } catch (e) {}

        // NUDE_STILLS.md: the looping char_nude_*.webm shipped a baked-in
        // horizontal squash (Wan I2V didn't hold the 2:3 still aspect -> the
        // figure renders +26-36% too wide, foundby:VPS 0.2.8.6) AND were opaque.
        // The paper-doll Phase-2 nude bases are the approved, correctly-
        // proportioned, transparent stills on the same axes -> render the base
        // layer only so the static nude view frames the figure identically to
        // the dressed doll. masc has no doll base yet -> legacy sprites. Flag
        // default-on; '0' restores the legacy 5-slot sprite stack for A/B.
        const useDollStill = localStorage.getItem('PnO_NudeDollStill') !== '0'
            && !mascClip && !_nudeDollStillFailed;

        if (animateNude) {
            // Video mode — single looping video based on state.
            // v9 (0.2.9) regen dropped the fem-tier axis: the nude clips are
            // base-tier only, with feminisation reading through boobs/longhair/
            // cage exactly like the doll stills. The old fem25/fem50/fem75 loops
            // were the squashed set and have been retired, so always resolve base.
            const tierPart = 'base';
            const arousalPart = aroused ? 'aroused' : 'soft';
            const chastPart = chastity ? '_chastity' : '';

            // Most-specific-first candidate chain; onerror walks down to base,
            // then to the sprite paperdoll.
            const baseName = `char_nude_${tierPart}_${arousalPart}${chastPart}`;
            const candidates = [];
            // Suffix combos via the shared appearance.js name-builder —
            // longhair (bit 1) + boobs (bit 2) axes only: chastity is baked
            // into the stem string, masc is the special clip below. The
            // descending submask walk yields most-specific first
            // (_longhair_boobs → _boobs → _longhair → stem).
            const lbMask = (longhair ? (1 << 1) : 0) | (boobs ? (1 << 2) : 0);
            const pushVariantChain = (stem) => {
                for (let m = lbMask; m > 0; m = (m - 1) & lbMask) {
                    candidates.push(appearanceState.buildVariantName(stem, m));
                }
                candidates.push(stem);
            };
            if (mascClip && tierPart === 'base' && !chastity && !boobs) {
                candidates.push(appearanceState.buildVariantName(`char_nude_base_${arousalPart}`, 1 << 3));
            }
            // v7 dirty axis: cum-covered loops, BASE body for all tiers (heavy
            // coverage hides tier differences), arousal collapsed. is_dirty is
            // an observed flag. Missing files fall through to the clean chain.
            if (variableManager.getFlag('is_dirty') && !mascClip) {
                pushVariantChain(`char_nude_dirty${chastPart}`);
            }
            pushVariantChain(baseName);

            const video = document.createElement('video');
            // v0.2.8+: transparent VP9-alpha WebM cutouts (TRANSPARENT_VIDEO.md pipeline)
            // replace the old opaque mp4 loops. onerror still falls back to sprites.
            let _candIdx = 0;
            video.src = `assets/videos/${candidates[_candIdx]}.webm`;
            video.loop = true;
            video.muted = true;
            video.playsInline = true;
            video.setAttribute('playsinline', '');
            video.autoplay = true;
            video.preload = 'metadata';
            video.disableRemotePlayback = true;
            video.classList.add('video-fade-in');
            video.style.cssText = 'width:100%;border-radius:8px;';
            // Robust reveal (mobile `loadeddata` may never fire under metadata
            // preload + suppressed autoplay) — see story-content _addVideo.
            const _revealCP = () => video.classList.add('is-loaded');
            video.addEventListener('loadedmetadata', _revealCP, { once: true });
            video.addEventListener('loadeddata', _revealCP, { once: true });
            setTimeout(() => { if (document.contains(video)) _revealCP(); }, 1500);
            let _retriedFinal = false;
            video.onerror = () => {
                // Walk down the variant chain first (missing _longhair/_boobs combo
                // falls back to the next-most-specific clip), then to sprites.
                _candIdx += 1;
                if (_candIdx < candidates.length) {
                    video.src = `assets/videos/${candidates[_candIdx]}.webm`;
                    return;
                }
                // All candidates failed. The base clip ships in every build,
                // so this is almost always a transient BunnyCDN 403 — re-issue
                // the current (base) src once before degrading, same 600ms
                // load() recipe as story-content/gallery videos.
                if (!_retriedFinal) {
                    _retriedFinal = true;
                    setTimeout(() => {
                        try { video.load(); } catch (e) { /* sprites on next error */ }
                    }, 600);
                    return;
                }
                // Sprites for THIS session only — a single network hiccup must
                // not permanently override the player's animation preference
                // (pre-fix this wrote CharAnimateNude=false to localStorage).
                _nudeVideoSessionFallback = true;
                _applyNudeView(true);
            };
            nudePanel.appendChild(video);
        } else if (useDollStill) {
            // Paper-doll nude base still (NUDE_STILLS.md). Reuses the dressed
            // doll's .paper-doll-layer framing (object-fit:contain) so both
            // views frame the figure identically. resolveImage('doll', …) is
            // tar-safe (doll ships tar-only, no loose copies).
            const wrap = document.createElement('div');
            wrap.className = 'paper-doll nude-doll-still';
            const cage = chastity ? '_chastity' : '';
            const sfx = (longhair ? '_longhair' : '') + (boobs ? '_boobs' : '');
            const ar = aroused ? '_aroused' : '';
            // is_dirty (cum-covered) is the OUTERMOST axis and collapses arousal
            // (heavy coverage hides the soft/aroused difference) — mirrors the
            // video `char_nude_dirty` stem. Dirty stills are tried first; if a
            // dirty variant is missing the walk falls back to the clean
            // aroused→soft chain so the figure never blanks.
            const dirty = variableManager.getFlag('is_dirty');
            const cand = [];
            const push = (s) => { if (s && !cand.includes(s)) cand.push(s); };
            if (dirty) {
                push(`doll_base${cage}${sfx}_dirty`);
                if (longhair && boobs) {
                    push(`doll_base${cage}_longhair_dirty`);
                    push(`doll_base${cage}_boobs_dirty`);
                }
                push(`doll_base${cage}_dirty`);
                push('doll_base_dirty');
            }
            // Most-specific first, then walk down: aroused→soft, drop boobs,
            // drop longhair, drop cage. Every soft combo exists; the _aroused
            // variants fill in as the stills land (NUDE_STILLS.md gen list).
            push(`doll_base${cage}${sfx}${ar}`);
            push(`doll_base${cage}${sfx}`);
            if (longhair && boobs) {
                push(`doll_base${cage}_longhair${ar}`); push(`doll_base${cage}_longhair`);
                push(`doll_base${cage}_boobs${ar}`);    push(`doll_base${cage}_boobs`);
            }
            push(`doll_base${cage}`);
            push('doll_base');
            let url = null;
            for (const c of cand) { url = resolveImage('doll', c); if (url) break; }
            if (url) {
                const img = document.createElement('img');
                img.className = 'paper-doll-layer';
                img.alt = '';
                img.decoding = 'async';
                img.src = url;
                // Genuine load failure (not tar-not-ready) -> sprites this
                // session without nuking the toggle.
                img.onerror = () => { _nudeDollStillFailed = true; _applyNudeView(true); };
                wrap.appendChild(img);
                nudePanel.appendChild(wrap);
            } else {
                // tar not ready yet -> sprite stack this render; the next
                // refreshView re-resolves once the tar lands.
                _renderLegacyNudeSprites(nudePanel, sprites);
            }
        } else {
            _renderLegacyNudeSprites(nudePanel, sprites);
        }

        // Animate is driven by the play/pause overlay button (btn-nude-play);
        // keep the overlay glyph + tooltips in sync.
        updateOverlayUI();
    } else {
        // Restore the dressed paper-doll. The nude-view (if mounted) is released
        // and removed; the doll box becomes visible again.
        if (dollBox) dollBox.style.display = '';
        if (nudePanel) { _releaseNudeVideos(nudePanel); nudePanel.remove(); }
    }
    // Status badges (hair/nails/laser/HRT) refresh on every view swap — they
    // sit under the figure in both views.
    try { refreshAccessories(); } catch (e) { /* story not initialised yet */ }
}

// ============================================
// Panel mode: nude / clothed (Paper-Doll)
// 0.2.9: the Nude/Clothed tab bar is gone — the eye button is the sole
// dressed⇄nude toggle. setPanelMode stays the single source of truth (the eye,
// a settings checkbox, and a legacy test event all route through it).
// ============================================
const PANEL_TAB_KEY = 'PnO_CharPanelTab';
let _subMode = 'clothed';   // last non-nude mode (for restore out of nude)

function setPanelMode(mode) {
    // Unknown / legacy values (e.g. a persisted 'icons') coerce to clothed.
    if (mode !== 'nude' && mode !== 'clothed') mode = 'clothed';
    if (mode !== 'nude') _subMode = mode;
    try { localStorage.setItem(PANEL_TAB_KEY, mode); } catch (e) { /* settings only */ }
    toggleNudeView(mode === 'nude');
    updateOverlayUI();
}

// Image-overlay controls (eye + play/pause, top-right of the character image).
function _isNudeMode() { try { return localStorage.getItem(PANEL_TAB_KEY) === 'nude'; } catch (e) { return false; } }
function _animPrefOn() {
    const cur = localStorage.getItem('CharAnimateNude');
    return (cur === 'true' || (cur === null && localStorage.getItem('PnO_ReduceMotion') !== '1'));
}
// Eye: toggle nude <-> last dressed view (hides clothing, never unequips).
function toggleNudeMode() { setPanelMode(_isNudeMode() ? _subMode : 'nude'); }
// Play/Pause the nude ANIMATION only. Does NOT switch nude<->clothed — that's the
// eye's job. Default is "play" (animated). So animated=on + nude=off shows the
// normal paper doll; clicking the eye then shows the (animated) nude.
function toggleAnimate() {
    try { localStorage.setItem('CharAnimateNude', _animPrefOn() ? 'false' : 'true'); } catch (e) {}
    _nudeVideoSessionFallback = false;        // explicit user action — retry videos
    if (_isNudeMode()) _applyNudeView(true);  // re-render only when the nude view is showing
    updateOverlayUI();
}
function updateOverlayUI() {
    const isNude = _isNudeMode(), animOn = _animPrefOn();
    const eye = document.getElementById('btn-nude-eye');
    const play = document.getElementById('btn-nude-play');
    if (eye) {
        eye.classList.toggle('active', isNude);
        // Tooltip reflects what the click DOES next: show dressed when nude, show nude when dressed.
        eye.title = isNude
            ? (L.get('char.show_dressed') || 'Show Dressed')
            : (L.get('char.show_nude') || 'Show Nude');
    }
    if (play) {
        // State glyph (independent of nude): ▶ = animation on (default), ⏸ = static.
        play.innerHTML = animOn ? '&#9654;' : '&#9208;';
        play.classList.toggle('active', animOn);
        play.title = animOn
            ? (L.get('char.animate.on_tooltip') || 'Animation enabled')
            : (L.get('char.animate.off_tooltip') || 'Animation disabled');
    }
}

function restorePanelMode() {
    let m = 'clothed';
    try { m = localStorage.getItem(PANEL_TAB_KEY) || 'clothed'; } catch (e) { /* default */ }
    // A persisted legacy 'icons' value is coerced to 'clothed' inside setPanelMode.
    if (m !== 'nude') _subMode = 'clothed';
    setPanelMode(m);
}

// ============================================
// Side-panel tabs: Char / Stats / Hints (0.2.9 — the old left stat/hints/actions
// column was merged into this one panel). The nude eye/play controls live inside
// the Char pane, so they hide automatically when Stats/Hints is active.
// ============================================
const SIDE_TAB_KEY = 'PnO_SidePanelTab';

function setSidePanelTab(tab) {
    if (tab === 'hints') tab = 'journal';   // legacy persisted value alias
    if (tab !== 'char' && tab !== 'stats' && tab !== 'journal') tab = 'char';
    try { localStorage.setItem(SIDE_TAB_KEY, tab); } catch (e) { /* settings only */ }
    document.querySelectorAll('.panel-tabs .panel-tab').forEach(b =>
        b.classList.toggle('active', b.dataset.tab === tab));
    document.querySelectorAll('.char-panel-scroll .tab-pane').forEach(p =>
        p.classList.toggle('active', p.dataset.tab === tab));
    // Fresh scroll position when switching into a (possibly tall) pane.
    const scroll = document.querySelector('.char-panel-scroll');
    if (scroll) scroll.scrollTop = 0;
}

function restoreSidePanelTab() {
    let t = 'char';
    try { t = localStorage.getItem(SIDE_TAB_KEY) || 'char'; } catch (e) { /* default */ }
    setSidePanelTab(t);
}

export const characterPanelUI = {
    updateSlots: updateCharacterPanelSlots,
    refreshAccessories,
    toggleNudeView,
    setPanelMode,
    restorePanelMode,
    toggleNudeMode,
    toggleAnimate,
    setSidePanelTab,
    restoreSidePanelTab,
};
